import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { extractApiErrorMessage } from 'utils/helpers/apiError';
import GradeCategoryAPI from 'api/grade-category.api';
import { type GradeCategoryRecord } from './types';

interface Props {
  open: boolean;
  record: GradeCategoryRecord | null;
  onClose: () => void;
  onSaved: (updated: GradeCategoryRecord) => void;
}

interface FormErrors {
  minGrade?: string;
  maxGrade?: string;
}

export const EditGradeCategoryModal = ({
  open,
  record,
  onClose,
  onSaved,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const gradeCategoryApi = useRef(new GradeCategoryAPI()).current;
  const [minGrade, setMinGrade] = useState('');
  const [maxGrade, setMaxGrade] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (record) {
      setMinGrade(String(record.minGrade));
      setMaxGrade(String(record.maxGrade));
      setErrors({});
    }
  }, [record]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const validate = (): boolean => {
    const next: FormErrors = {};
    const min = parseFloat(minGrade);
    const max = parseFloat(maxGrade);
    if (minGrade.trim() === '' || isNaN(min))
      next.minGrade = t('grade_category_field_required');
    if (maxGrade.trim() === '' || isNaN(max))
      next.maxGrade = t('grade_category_field_required');
    if (!next.minGrade && !next.maxGrade && min >= max) {
      next.maxGrade = t('grade_category_min_max_invalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!record) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const updated = await gradeCategoryApi.update(record.gradeCategory, {
        minGrade: parseFloat(minGrade),
        maxGrade: parseFloat(maxGrade),
      });
      onSaved(updated);
    } catch (e) {
      const message = extractApiErrorMessage(
        e,
        t('grade_category_toast_update_failed')
      );
      dispatch(showNotification({ message, type: 'error' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.25),
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('grade_category_edit_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {record?.gradeCategory}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('grade_category_field_code')}
              fullWidth
              size="small"
              value={record?.gradeCategory ?? ''}
              disabled
              helperText={t('grade_category_code_immutable')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('grade_category_field_min')}
              fullWidth
              size="small"
              type="number"
              autoFocus
              value={minGrade}
              onChange={(e) => {
                setMinGrade(e.target.value);
              }}
              error={!!errors.minGrade}
              helperText={errors.minGrade}
              inputProps={{
                step: 'any',
                onWheel: (e) => {
                  (e.target as HTMLInputElement).blur();
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('grade_category_field_max')}
              fullWidth
              size="small"
              type="number"
              value={maxGrade}
              onChange={(e) => {
                setMaxGrade(e.target.value);
              }}
              error={!!errors.maxGrade}
              helperText={errors.maxGrade}
              inputProps={{
                step: 'any',
                onWheel: (e) => {
                  (e.target as HTMLInputElement).blur();
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={submitting}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('grade_category_cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
        >
          {t('grade_category_save_changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
